import React, { Component } from 'react';
import { Button, Grid, Header, Icon, Message, Modal, Segment, Form, TextArea } from 'semantic-grab-ui/src';
import yaml from 'js-yaml';
import _ from 'lodash';

// Type definitions
interface ConfigKey {
  key: string;
  title: string;
  description: string;
  icon: string;
  color: string;
}

interface UserState {
  name?: string;
  email?: string;
  accessToken?: string;
  refreshToken?: string;
  relogin?: boolean;
  userPermissions?: any;
}

interface EventConditionActionConfig {
  config?: any;
  version?: number;
  createdAt?: string;
  createdBy?: string;
  comment?: string;
}

interface EventConditionActionConfigs {
  [key: string]: EventConditionActionConfig;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onConfirm?: (comment?: string) => void;
  onSaveConfigurations?: (actionData: any) => Promise<any>;
  configs: { [key: string]: string };
  originalConfigs: { [key: string]: string };
  configKeys: ConfigKey[];
  user?: UserState;
  eventConditionActionConfigs?: EventConditionActionConfigs;
}

interface State {
  comment: string;
  isSaving: boolean;
}

interface DiffItem {
  type: 'added' | 'removed' | 'modified' | 'unchanged' | 'separator';
  originalLine?: string;
  newLine?: string;
  originalLineNumber?: number | string;
  newLineNumber?: number | string;
}

export class DiffModal extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      comment: '',
      isSaving: false
    };
  }

  // Handle comment change
  handleCommentChange = (e: any, { value }: { value: string }) => {
    this.setState({ comment: value });
  };

  // Handle modal close - reset comment
  handleClose = () => {
    this.setState({ comment: '', isSaving: false });
    this.props.onClose();
  };

  // Check if current environment is PRD
  isPrdEnvironment = (): boolean => {
    return _.get(window, 'Grab.config.ENV', 'stg') === 'prd';
  };

  // Handle confirm and save with backend integration
  handleConfirmAndSave = async () => {
    const { onConfirm } = this.props;
    const { comment } = this.state;

    // Show confirmation alert for PRD environment
    if (this.isPrdEnvironment()) {
      const confirmed = window.confirm(
        '⚠️ WARNING: You are making changes in the PRODUCTION environment.\n\n' +
        'Are you sure you want to save these changes?'
      );
      if (!confirmed) {
        return;
      }
    }

    this.setState({ isSaving: true });

    try {
      // Convert YAML to JSON and send to backend
      await this.saveConfigurationsToBackend(comment);
      
      // Call the original onConfirm callback with comment if it exists
      if (onConfirm) {
        onConfirm(comment);
      }
      
      // Reset state and close modal
      this.setState({ comment: '', isSaving: false });
    } catch (error) {
      console.error('Failed to save configurations:', error);
      // TODO: Show user-friendly error message
      // For now, still call onConfirm to maintain existing behavior
      if (onConfirm) {
        onConfirm(comment);
      }
      this.setState({ isSaving: false });
    }
  };

  // Convert YAML configurations to JSON format (only for changed keys)
  convertConfigurationsToJSON = (configs: { [key: string]: string }, originalConfigs: { [key: string]: string }, configKeys: ConfigKey[]) => {
    const jsonConfigs: { [key: string]: any } = {};
    const changedKeys: string[] = [];
    
    // First, identify which keys have actually changed
    configKeys.forEach(config => {
      const configKey = config.key;
      const currentValue = configs[configKey] || '';
      const originalValue = originalConfigs[configKey] || '';
      
      if (currentValue !== originalValue) {
        changedKeys.push(configKey);
      }
    });
    
    // Only process the changed keys
    changedKeys.forEach(configKey => {
      const yamlContent = configs[configKey];
      
      try {
        if (yamlContent && yamlContent.trim()) {
          // Clean the YAML content by removing comments and top-level keys
          const cleanContent = yamlContent
            .replace(/^#.*$/gm, '') // Remove comment lines
            .replace(/^(eventTypes|conditions|actions|eventAcl|actionAcl):\s*$/m, '') // Remove top-level keys
            .trim();
          
          if (cleanContent) {
            // Parse YAML to JavaScript object (proper JSON format)
            const parsedYaml = yaml.load(cleanContent);
            jsonConfigs[configKey] = parsedYaml;
          } else {
            jsonConfigs[configKey] = {};
          }
        } else {
          jsonConfigs[configKey] = {};
        }
      } catch (error) {
        console.error(`Failed to convert ${configKey} from YAML to JSON:`, error);
        // Fallback to empty object for invalid YAML
        jsonConfigs[configKey] = {};
      }
    });
    
    return { jsonConfigs, changedKeys };
  };

  // Send configurations to backend following SetConfigurationRequest structure
  saveConfigurationsToBackend = async (comment: string) => {
    const { configs, originalConfigs, configKeys, onSaveConfigurations, user, eventConditionActionConfigs } = this.props;
    
    try {
      // Convert only changed YAML configurations to JSON
      const { jsonConfigs, changedKeys } = this.convertConfigurationsToJSON(configs, originalConfigs, configKeys);
      
      // Early return if no changes detected
      if (changedKeys.length === 0) {
        console.log('No configuration changes detected, skipping backend save');
        return { success: true, message: 'No changes to save' };
      }
      
      // Build configurations map following EventConditionActionRawConfigRequest structure
      const configurations: { [key: string]: any } = {};
      changedKeys.forEach(configKey => {
        // Get previous version from the backend response if available
        const backendConfig = eventConditionActionConfigs && eventConditionActionConfigs[configKey];
        const previousVersion = backendConfig && backendConfig.version ? backendConfig.version : 1;
        
        configurations[configKey] = {
          config: jsonConfigs[configKey],
          previousVersion: previousVersion
        };
      });

      // Prepare the data payload following SetConfigurationRequest structure
      const actionData = {
        eventConditionAction: {
          configurations: configurations,
          comment: comment || '',
          createdBy: user?.email // Following the same pattern as TridentCampaignForm
        },
        changedKeys: changedKeys // Add this for the parent component to know which keys changed
      };

      console.log(`Saving ${changedKeys.length} changed configuration(s):`, changedKeys);

      // Use Redux action pattern similar to changeCampaignStatus
      // The parent component should provide onSaveConfigurations prop that dispatches the action
      if (onSaveConfigurations && typeof onSaveConfigurations === 'function') {
        return await onSaveConfigurations(actionData);
      }
    } catch (error) {
      console.error('Failed to save configurations to backend:', error);
      throw error;
    }
  };

  // Generate proper diff with LCS algorithm for better line matching
  generateDiff = (originalText: string, newText: string, contextLines: number = 3): DiffItem[] => {
    if (!originalText && !newText) return [];
    
    const originalLines = originalText ? originalText.split('\n') : [];
    const newLines = newText ? newText.split('\n') : [];
    
    // Use a simple diff algorithm to detect added, removed, and modified lines
    const fullDiff = this.computeLineDiff(originalLines, newLines);
    
    // Filter to show only changed lines with context
    const contextualDiff = this.addContext(fullDiff, contextLines);
    return contextualDiff;
  };

  // Add context lines around changes and group them
  addContext = (diff: DiffItem[], contextLines: number): DiffItem[] => {
    const result: DiffItem[] = [];
    const changedIndices: number[] = [];
    
    // Find indices of all changed lines
    diff.forEach((item, index) => {
      if (item.type !== 'unchanged') {
        changedIndices.push(index);
      }
    });
    
    if (changedIndices.length === 0) {
      return []; // No changes to show
    }
    
    // Create ranges of lines to include (changed lines + context)
    const ranges: { start: number; end: number }[] = [];
    let currentRange: { start: number; end: number } | null = null;
    
    changedIndices.forEach(changeIndex => {
      const start = Math.max(0, changeIndex - contextLines);
      const end = Math.min(diff.length - 1, changeIndex + contextLines);
      
      if (currentRange && start <= currentRange.end + 1) {
        // Merge overlapping ranges
        currentRange.end = end;
      } else {
        // Start new range
        if (currentRange) ranges.push(currentRange);
        currentRange = { start, end };
      }
    });
    
    if (currentRange) ranges.push(currentRange);
    
    // Build the result with context and separators
    ranges.forEach((range, rangeIndex) => {
      // Add separator if not the first range
      if (rangeIndex > 0) {
        result.push({
          type: 'separator',
          originalLine: '...',
          newLine: '...',
          originalLineNumber: '...',
          newLineNumber: '...'
        });
      }
      
      // Add lines in this range
      for (let i = range.start; i <= range.end; i++) {
        result.push(diff[i]);
      }
    });
    
    return result;
  };

  // Simple diff algorithm to properly detect additions, deletions, and modifications
  computeLineDiff = (originalLines: string[], newLines: string[]): DiffItem[] => {
    const diff: DiffItem[] = [];
    let origIndex = 0;
    let newIndex = 0;
    
    while (origIndex < originalLines.length || newIndex < newLines.length) {
      const origLine = originalLines[origIndex];
      const newLine = newLines[newIndex];
      
      if (origIndex >= originalLines.length) {
        // Only new lines remain - all are additions
        diff.push({
          type: 'added',
          originalLine: undefined,
          newLine: newLine,
          originalLineNumber: undefined,
          newLineNumber: newIndex + 1
        });
        newIndex++;
      } else if (newIndex >= newLines.length) {
        // Only original lines remain - all are deletions
        diff.push({
          type: 'removed',
          originalLine: origLine,
          newLine: undefined,
          originalLineNumber: origIndex + 1,
          newLineNumber: undefined
        });
        origIndex++;
      } else if (origLine === newLine) {
        // Lines are identical
        diff.push({
          type: 'unchanged',
          originalLine: origLine,
          newLine: newLine,
          originalLineNumber: origIndex + 1,
          newLineNumber: newIndex + 1
        });
        origIndex++;
        newIndex++;
      } else {
        // Lines are different - need to determine if it's a modification or insertion/deletion
        // Look ahead to see if this line appears later in the other array
        const origLineInNew = newLines.indexOf(origLine, newIndex);
        const newLineInOrig = originalLines.indexOf(newLine, origIndex);
        
        if (origLineInNew !== -1 && newLineInOrig !== -1) {
          // Both lines appear later, choose the closer one
          const origDistance = origLineInNew - newIndex;
          const newDistance = newLineInOrig - origIndex;
          
          if (origDistance <= newDistance) {
            // Insert new lines until we reach the original line
            while (newIndex < origLineInNew) {
              diff.push({
                type: 'added',
                originalLine: undefined,
                newLine: newLines[newIndex],
                originalLineNumber: undefined,
                newLineNumber: newIndex + 1
              });
              newIndex++;
            }
          } else {
            // Remove original lines until we reach the new line
            while (origIndex < newLineInOrig) {
              diff.push({
                type: 'removed',
                originalLine: originalLines[origIndex],
                newLine: undefined,
                originalLineNumber: origIndex + 1,
                newLineNumber: undefined
              });
              origIndex++;
            }
          }
        } else if (origLineInNew !== -1) {
          // Original line appears later in new - insert new lines first
          diff.push({
            type: 'added',
            originalLine: undefined,
            newLine: newLine,
            originalLineNumber: undefined,
            newLineNumber: newIndex + 1
          });
          newIndex++;
        } else if (newLineInOrig !== -1) {
          // New line appears later in original - remove original line first
          diff.push({
            type: 'removed',
            originalLine: origLine,
            newLine: undefined,
            originalLineNumber: origIndex + 1,
            newLineNumber: undefined
          });
          origIndex++;
        } else {
          // Neither line appears later - this is a true modification
          diff.push({
            type: 'modified',
            originalLine: origLine,
            newLine: newLine,
            originalLineNumber: origIndex + 1,
            newLineNumber: newIndex + 1
          });
          origIndex++;
          newIndex++;
        }
      }
    }
    
    return diff;
  };

  // Highlight character differences within a line
  highlightLineDifferences = (originalLine?: string, newLine?: string): { original: string; new: string } => {
    if (!originalLine && !newLine) return { original: '', new: '' };
    if (!originalLine) return { original: '', new: `<span class="diff-added">${this.escapeHtml(newLine!)}</span>` };
    if (!newLine) return { original: `<span class="diff-removed">${this.escapeHtml(originalLine)}</span>`, new: '' };
    
    // Simple character-level highlighting
    if (originalLine === newLine) {
      return { original: this.escapeHtml(originalLine), new: this.escapeHtml(newLine) };
    }
    
    // For different lines, highlight the entire line
    return {
      original: `<span class="diff-removed">${this.escapeHtml(originalLine)}</span>`,
      new: `<span class="diff-added">${this.escapeHtml(newLine)}</span>`
    };
  };

  // Escape HTML to prevent XSS
  escapeHtml = (text: string): string => {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  };

  // Render line with highlighting
  renderLineWithHighlight = (line: any, type: string) => {
    const getLineStyle = (type: string) => {
      switch (type) {
        case 'added':
          return {
            backgroundColor: '#e6ffed',
            borderLeft: '4px solid #28a745',
            paddingLeft: '8px'
          };
        case 'removed':
          return {
            backgroundColor: '#ffeef0',
            borderLeft: '4px solid #d73a49',
            paddingLeft: '8px'
          };
        case 'modified':
          return {
            backgroundColor: '#fff5b4',
            borderLeft: '4px solid #ffc107',
            paddingLeft: '8px'
          };
        default:
          return {};
      }
    };

    return (
      <div
        key={`line-${line.lineNumber}`}
        style={{
          ...getLineStyle(type),
          padding: '2px 8px',
          margin: '1px 0',
          fontSize: '13px',
          fontFamily: 'Monaco, Menlo, "Ubuntu Mono", monospace',
          lineHeight: '1.4',
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word'
        }}
      >
        <span style={{ color: '#666', marginRight: '12px', fontSize: '11px' }}>
          {line.lineNumber}
        </span>
        <span dangerouslySetInnerHTML={{ __html: line.content }} />
      </div>
    );
  };

  render() {
    const { 
      isOpen, 
      onClose, 
      onConfirm, 
      configs, 
      originalConfigs, 
      configKeys 
    } = this.props;

    if (!isOpen) return null;

    // Calculate which configs have changes
    const changedConfigs = configKeys.filter(config => 
      configs[config.key] !== originalConfigs[config.key]
    );

    return (
      <Modal
        open={isOpen}
        onClose={this.handleClose}
        size="fullscreen"
        closeIcon
      >
        <Modal.Header>
          <Icon name="code branch" />
          Review Configuration Changes
          <div style={{ fontSize: '0.9em', fontWeight: 'normal', color: '#666', marginTop: '0.5em' }}>
            Review the changes below before saving. Only {changedConfigs.length} modified configuration(s) will be submitted to the backend.
          </div>
        </Modal.Header>
        
        <Modal.Content scrolling style={{ minHeight: '70vh' }}>
          <style jsx>{`
            .diff-added {
              background-color: #acf2bd;
              color: #155724;
              padding: 2px 4px;
              border-radius: 2px;
            }
            .diff-removed {
              background-color: #fdb8c0;
              color: #721c24;
              padding: 2px 4px;
              border-radius: 2px;
            }
            .diff-line-added {
              background-color: #e6ffed;
              border-left: 4px solid #28a745;
            }
            .diff-line-removed {
              background-color: #ffeef0;
              border-left: 4px solid #d73a49;
            }
            .diff-line-modified {
              background-color: #fff5b4;
              border-left: 4px solid #ffc107;
            }
          `}</style>
          
          {changedConfigs.length === 0 ? (
            <Message info>
              <Icon name="info circle" />
              No changes detected. All configurations are identical to their saved versions.
            </Message>
          ) : (
            <div>
                             {changedConfigs.map((config) => {
                 return (
                  <Segment key={config.key} style={{ marginBottom: '2rem' }}>
                    <Header as="h3" style={{ marginBottom: '1rem' }}>
                      <Icon name={config.icon as any} color={config.color as any} />
                      <Header.Content>
                        {config.title}
                        <Header.Subheader>{config.description}</Header.Subheader>
                      </Header.Content>
                    </Header>
                    
                    <div style={{ 
                      border: '1px solid #d4d4d5',
                      borderRadius: '4px',
                      overflow: 'hidden',
                      fontFamily: 'Monaco, Menlo, "Ubuntu Mono", monospace',
                      fontSize: '13px'
                    }}>
                      <div style={{
                        display: 'flex',
                        backgroundColor: '#fafbfc',
                        borderBottom: '1px solid #d4d4d5',
                        fontSize: '12px',
                        fontWeight: 'bold'
                      }}>
                        <div style={{ 
                          flex: 1, 
                          padding: '8px 12px',
                          borderRight: '1px solid #d4d4d5',
                          color: '#d73a49'
                        }}>
                          <Icon name="minus" /> Original Configuration
                        </div>
                        <div style={{ 
                          flex: 1, 
                          padding: '8px 12px',
                          color: '#28a745'
                        }}>
                          <Icon name="plus" /> New Configuration
                        </div>
                      </div>
                      
                                             <div style={{ 
                         display: 'flex',
                         maxHeight: '400px',
                         overflow: 'auto'
                       }}>
                         {/* Generate proper diff */}
                         {(() => {
                           const diff = this.generateDiff(originalConfigs[config.key], configs[config.key]);
                           
                           return (
                             <React.Fragment>
                               {/* Original (Left Side) */}
                               <div style={{ 
                                 flex: 1,
                                 borderRight: '1px solid #d4d4d5',
                                 backgroundColor: '#fafbfc',
                                 minHeight: '100%'
                               }}>
                                 {diff.map((diffItem, index) => {
                                   if (diffItem.type === 'separator') {
                                     // Separator line to indicate omitted content
                                     return (
                                       <div
                                         key={`sep-orig-${index}`}
                                         style={{
                                           padding: '8px 12px',
                                           margin: 0,
                                           borderBottom: '1px solid #ddd',
                                           minHeight: '20px',
                                           lineHeight: '1.4',
                                           fontSize: '13px',
                                           color: '#999',
                                           backgroundColor: '#f5f5f5',
                                           textAlign: 'center',
                                           fontStyle: 'italic',
                                           borderTop: '1px solid #ddd'
                                         }}
                                       >
                                         <span style={{ fontSize: '12px' }}>• • •</span>
                                       </div>
                                     );
                                   } else if (diffItem.type === 'added') {
                                     // Added lines - show empty line on left side
                                     return (
                                       <div
                                         key={`orig-${index}`}
                                         style={{
                                           padding: '2px 12px',
                                           margin: 0,
                                           borderBottom: '1px solid #eee',
                                           minHeight: '20px',
                                           lineHeight: '1.4',
                                           fontSize: '13px',
                                           color: '#ccc',
                                           backgroundColor: '#f8f8f8'
                                         }}
                                       >
                                         <span style={{ color: '#999', marginRight: '8px', fontSize: '11px', minWidth: '30px', display: 'inline-block' }}>
                                           
                                         </span>
                                         <span style={{ color: '#ccc' }}>...</span>
                                       </div>
                                     );
                                   } else if (diffItem.type === 'removed') {
                                     // Removed lines - highlight in red on left side
                                     return (
                                       <div
                                         key={`orig-${index}`}
                                         className="diff-line-removed"
                                         style={{
                                           padding: '2px 12px',
                                           margin: 0,
                                           borderBottom: '1px solid #eee',
                                           minHeight: '20px',
                                           lineHeight: '1.4',
                                           whiteSpace: 'pre-wrap',
                                           wordBreak: 'break-word',
                                           fontSize: '13px',
                                           backgroundColor: '#ffeef0',
                                           borderLeft: '4px solid #d73a49'
                                         }}
                                       >
                                         <span style={{ color: '#666', marginRight: '8px', fontSize: '11px', minWidth: '30px', display: 'inline-block' }}>
                                           {diffItem.originalLineNumber}
                                         </span>
                                         <span className="diff-removed">
                                           {diffItem.originalLine}
                                         </span>
                                       </div>
                                     );
                                   } else {
                                     // Unchanged or modified lines
                                     let content = this.escapeHtml(diffItem.originalLine || '');
                                     let backgroundColor = '#fafbfc';
                                     let borderLeft = 'none';
                                     
                                     if (diffItem.type === 'modified') {
                                       const highlighted = this.highlightLineDifferences(diffItem.originalLine, diffItem.newLine);
                                       content = highlighted.original;
                                       backgroundColor = '#fff5b4';
                                       borderLeft = '4px solid #ffc107';
                                     }
                                     
                                     return (
                                       <div
                                         key={`orig-${index}`}
                                         style={{
                                           padding: '2px 12px',
                                           margin: 0,
                                           borderBottom: '1px solid #eee',
                                           minHeight: '20px',
                                           lineHeight: '1.4',
                                           whiteSpace: 'pre-wrap',
                                           wordBreak: 'break-word',
                                           fontSize: '13px',
                                           backgroundColor,
                                           borderLeft
                                         }}
                                       >
                                         <span style={{ color: '#666', marginRight: '8px', fontSize: '11px', minWidth: '30px', display: 'inline-block' }}>
                                           {diffItem.originalLineNumber}
                                         </span>
                                         <span dangerouslySetInnerHTML={{ __html: content }} />
                                       </div>
                                     );
                                   }
                                 })}
                               </div>
                               
                               {/* New (Right Side) */}
                               <div style={{ flex: 1, backgroundColor: '#f8f9fa' }}>
                                 {diff.map((diffItem, index) => {
                                   if (diffItem.type === 'separator') {
                                     // Separator line to indicate omitted content
                                     return (
                                       <div
                                         key={`sep-new-${index}`}
                                         style={{
                                           padding: '8px 12px',
                                           margin: 0,
                                           borderBottom: '1px solid #ddd',
                                           minHeight: '20px',
                                           lineHeight: '1.4',
                                           fontSize: '13px',
                                           color: '#999',
                                           backgroundColor: '#f5f5f5',
                                           textAlign: 'center',
                                           fontStyle: 'italic',
                                           borderTop: '1px solid #ddd'
                                         }}
                                       >
                                         <span style={{ fontSize: '12px' }}>• • •</span>
                                       </div>
                                     );
                                   } else if (diffItem.type === 'removed') {
                                     // Removed lines - show empty line on right side
                                     return (
                                       <div
                                         key={`new-${index}`}
                                         style={{
                                           padding: '2px 12px',
                                           margin: 0,
                                           borderBottom: '1px solid #eee',
                                           minHeight: '20px',
                                           lineHeight: '1.4',
                                           fontSize: '13px',
                                           color: '#ccc',
                                           backgroundColor: '#f8f8f8'
                                         }}
                                       >
                                         <span style={{ color: '#999', marginRight: '8px', fontSize: '11px', minWidth: '30px', display: 'inline-block' }}>
                                           
                                         </span>
                                         <span style={{ color: '#ccc' }}>...</span>
                                       </div>
                                     );
                                   } else if (diffItem.type === 'added') {
                                     // Added lines - highlight in green on right side
                                     return (
                                       <div
                                         key={`new-${index}`}
                                         className="diff-line-added"
                                         style={{
                                           padding: '2px 12px',
                                           margin: 0,
                                           borderBottom: '1px solid #eee',
                                           minHeight: '20px',
                                           lineHeight: '1.4',
                                           whiteSpace: 'pre-wrap',
                                           wordBreak: 'break-word',
                                           fontSize: '13px',
                                           backgroundColor: '#e6ffed',
                                           borderLeft: '4px solid #28a745'
                                         }}
                                       >
                                         <span style={{ color: '#666', marginRight: '8px', fontSize: '11px', minWidth: '30px', display: 'inline-block' }}>
                                           {diffItem.newLineNumber}
                                         </span>
                                         <span className="diff-added">
                                           {diffItem.newLine}
                                         </span>
                                       </div>
                                     );
                                   } else {
                                     // Unchanged or modified lines
                                     let content = this.escapeHtml(diffItem.newLine || '');
                                     let backgroundColor = '#f8f9fa';
                                     let borderLeft = 'none';
                                     
                                     if (diffItem.type === 'modified') {
                                       const highlighted = this.highlightLineDifferences(diffItem.originalLine, diffItem.newLine);
                                       content = highlighted.new;
                                       backgroundColor = '#fff5b4';
                                       borderLeft = '4px solid #ffc107';
                                     }
                                     
                                     return (
                                       <div
                                         key={`new-${index}`}
                                         style={{
                                           padding: '2px 12px',
                                           margin: 0,
                                           borderBottom: '1px solid #eee',
                                           minHeight: '20px',
                                           lineHeight: '1.4',
                                           whiteSpace: 'pre-wrap',
                                           wordBreak: 'break-word',
                                           fontSize: '13px',
                                           backgroundColor,
                                           borderLeft
                                         }}
                                       >
                                         <span style={{ color: '#666', marginRight: '8px', fontSize: '11px', minWidth: '30px', display: 'inline-block' }}>
                                           {diffItem.newLineNumber}
                                         </span>
                                         <span dangerouslySetInnerHTML={{ __html: content }} />
                                       </div>
                                     );
                                   }
                                 })}
                               </div>
                             </React.Fragment>
                           );
                         })()}
                       </div>
                    </div>
                  </Segment>
                );
              })}
            </div>
          )}
        </Modal.Content>
        
        <Modal.Content>
          <Form>
            <Form.Field>
              <label>Comment</label>
              <TextArea
                placeholder="Add a comment about these changes..."
                value={this.state.comment}
                onChange={this.handleCommentChange}
                rows={3}
                style={{ minHeight: '80px' }}
              />
            </Form.Field>
          </Form>
        </Modal.Content>
        
        <Modal.Actions>
          <Button onClick={this.handleClose}>
            <Icon name="cancel" />
            Cancel
          </Button>
          <Button 
            color="green" 
            onClick={this.handleConfirmAndSave}
            disabled={changedConfigs.length === 0}
            loading={this.state.isSaving}
          >
            <Icon name="checkmark" />
            Confirm & Save Changes
          </Button>
        </Modal.Actions>
      </Modal>
    );
  }
}

export default DiffModal;
